import React, { useState, useEffect, useCallback } from 'react';
import { partiesAPI } from '../services/api';
import { useToast } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import { SkeletonTable, SkeletonCards } from '../components/Skeleton';

// Utility function to format date as DD MMM, YYYY
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

const Parties = () => {
  const toast = useToast();
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, id: null });
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingParty, setEditingParty] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 15,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [formData, setFormData] = useState({
    name: '',
    details: ''
  });

  // Combined effect with debounce for search, immediate for pagination
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchParties();
    }, searchTerm ? 1000 : 0); // Debounce only for search, immediate for pagination

    return () => clearTimeout(timeoutId);
  }, [pagination.currentPage, searchTerm]);

  const fetchParties = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: 15,
        ...(searchTerm && { search: searchTerm })
      };

      const response = await partiesAPI.getAll(params);

      if (response.data.parties) {
        // New paginated response format
        setParties(response.data.parties);
        setPagination(prev => ({
          ...prev,
          totalPages: response.data.pagination.totalPages,
          totalItems: response.data.pagination.totalItems,
          hasNextPage: response.data.pagination.hasNextPage,
          hasPrevPage: response.data.pagination.hasPrevPage
        }));
      } else {
        // Fallback for old response format
        setParties(response.data);
      }
    } catch (error) {
      console.error('Error fetching parties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Party name is required');
      return;
    }

    try {
      if (editingParty) {
        await partiesAPI.update(editingParty._id, formData);
      } else {
        await partiesAPI.create(formData);
      }

      toast.success(editingParty ? 'Party updated' : 'Party created');
      setFormData({ name: '', details: '' });
      setEditingParty(null);
      setShowForm(false);
      fetchParties();
    } catch (error) {
      console.error('Error saving party:', error);
      toast.error('Error saving party');
    }
  };

  const handleEdit = (party) => {
    setEditingParty(party);
    setFormData({
      name: party.name,
      details: party.details || ''
    });
    setShowForm(true);
  };

  const handleDelete = (id) => {
    setConfirmDialog({ isOpen: true, id });
  };

  const confirmDelete = async () => {
    try {
      await partiesAPI.delete(confirmDialog.id);
      toast.success('Party deleted');
      fetchParties();
    } catch (error) {
      console.error('Error deleting party:', error);
      toast.error('Error deleting party');
    } finally {
      setConfirmDialog({ isOpen: false, id: null });
    }
  };

  const handleCancel = () => {
    setFormData({ name: '', details: '' });
    setEditingParty(null);
    setShowForm(false);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page on search
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-brand-900">Parties</h1>
          <p className="text-gray-500 mt-1 sm:mt-2 text-sm sm:text-base">Manage your parties</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="w-full sm:w-auto bg-brand-500 text-white px-4 py-2.5 rounded-xl hover:bg-brand-600 shadow-sm shadow-brand-500/25 transition-colors text-base font-medium"
        >
          Add Party
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl border border-brand-100 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search parties by name or details..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full px-3.5 py-2.5 border border-brand-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-base text-brand-900 placeholder-gray-400"
            />
          </div>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="w-full sm:w-auto px-4 py-2.5 bg-white text-gray-700 border border-brand-100 hover:bg-brand-50 rounded-xl transition-colors text-base font-medium"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <>
          <div className="hidden sm:block">
            <SkeletonTable rows={5} cols={4} />
          </div>
          <div className="block sm:hidden">
            <SkeletonCards count={3} />
          </div>
        </>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-2xl border border-brand-100 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-brand-900 mb-4">
            {editingParty ? 'Edit Party' : 'Add New Party'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Party Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3.5 py-2.5 border border-brand-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-base text-brand-900"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Details
              </label>
              <textarea
                name="details"
                value={formData.details}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3.5 py-2.5 border border-brand-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-base text-brand-900"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                className="w-full sm:w-auto bg-brand-500 text-white px-4 py-2.5 rounded-xl hover:bg-brand-600 shadow-sm shadow-brand-500/25 transition-colors text-base font-medium"
              >
                {editingParty ? 'Update Party' : 'Add Party'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="w-full sm:w-auto bg-white text-gray-700 border border-brand-100 hover:bg-brand-50 px-4 py-2.5 rounded-xl transition-colors text-base font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Parties List */}
      {!loading && (
        <>
          {/* Desktop Table View */}
          <div className="hidden sm:block bg-white rounded-2xl border border-brand-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-brand-100">
                <thead className="bg-brand-50/50">
                  <tr>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Party Name
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-brand-100">
                  {parties.map((party) => (
                    <tr key={party._id} className="hover:bg-brand-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-brand-900">
                        {party.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {party.details || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(party.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEdit(party)}
                          className="px-2.5 py-1 rounded-lg text-xs font-medium bg-green-50 text-green-600 hover:bg-green-100 transition-colors mr-2"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(party._id)}
                          className="px-2.5 py-1 rounded-lg text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="block sm:hidden space-y-4">
            {parties.map((party) => (
              <div key={party._id} className="bg-white rounded-2xl border border-brand-100 p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-semibold text-brand-900">{party.name}</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(party)}
                      className="px-2.5 py-1 rounded-lg text-xs font-medium bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(party._id)}
                      className="px-2.5 py-1 rounded-lg text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                {party.details && (
                  <p className="text-gray-500 text-sm mb-2">{party.details}</p>
                )}
                <p className="text-xs text-gray-500">
                  Created: {formatDate(party.createdAt)}
                </p>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="bg-white rounded-2xl border border-brand-100 px-4 py-3 flex items-center justify-between sm:px-6">
              {/* Mobile Pagination */}
              <div className="flex sm:hidden justify-between items-center w-full">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrevPage}
                  className="relative inline-flex items-center px-3 py-2 border border-brand-100 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-brand-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                  className="relative inline-flex items-center px-3 py-2 border border-brand-100 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-brand-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>

              {/* Desktop Pagination */}
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-500">
                    Showing{' '}
                    <span className="font-medium text-brand-900">
                      {(pagination.currentPage - 1) * pagination.itemsPerPage + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium text-brand-900">
                      {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium text-brand-900">{pagination.totalItems}</span>{' '}
                    results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-xl shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={!pagination.hasPrevPage}
                      className="relative inline-flex items-center px-3 py-2 rounded-l-xl border border-brand-100 bg-white text-sm font-medium text-gray-500 hover:bg-brand-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>

                    {/* Page Numbers - Show max 5 pages */}
                    {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                      let page;
                      if (pagination.totalPages <= 5) {
                        page = i + 1;
                      } else if (pagination.currentPage <= 3) {
                        page = i + 1;
                      } else if (pagination.currentPage >= pagination.totalPages - 2) {
                        page = pagination.totalPages - 4 + i;
                      } else {
                        page = pagination.currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-colors ${
                            page === pagination.currentPage
                              ? 'z-10 bg-brand-500 border-brand-500 text-white'
                              : 'bg-white border-brand-100 text-gray-500 hover:bg-brand-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                      className="relative inline-flex items-center px-3 py-2 rounded-r-xl border border-brand-100 bg-white text-sm font-medium text-gray-500 hover:bg-brand-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Delete Party"
        message="Are you sure you want to delete this party? This action cannot be undone."
        onConfirm={confirmDelete}
        onCancel={() => setConfirmDialog({ isOpen: false, id: null })}
      />
    </div>
  );
};

export default Parties;
